import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import B2BComponent from 'components/B2BComponents/B2BComponent';
import { useIsB2B } from 'components/B2BComponents/useIsB2B';
import {
  getEstimateB2BDiscounts,
  getEstimateB2BPrices,
  getEstimateBundleOrRecyclingSectionPriceVatExcl,
  getEstimateBundleOrRecyclingSectionVat,
  getEstimateLaborTimeSectionPriceVatExcl,
  getEstimateLaborTimeSectionVat,
  getEstimateOperationLaborTimesSectionPriceVatExcl,
  getEstimateOperationLaborTimesSectionVat,
  getEstimateOperationReferenceSectionPriceVatExcl,
  getEstimateOperationReferencesVat,
  getEstimateReferenceDiscountSumMargin,
  getEstimateReferenceSectionPriceVatExcl,
  getEstimateReferenceSectionVat,
  getReferences,
  getTotalMargin,
  getTotalMarginPercentage,
} from 'domains/estimate/Estimate.store';
import { GarageView, getCurrency } from 'domains/user';
import TotalMarginLine from 'pages/EstimatePage/DetailsSection/TotalMarginLine';
import {
  getTotalVatExclPriceForClient,
  getTotalVatPriceForClient,
} from 'pages/EstimatePage/OrderEstimate/B2BEstimateOrderValidationStep';
import { Flex, LightCard } from 'UI';
import { textFormatter } from 'utils';
import DetailsSectionLine from './DetailsSectionLine';

export interface DetailsSectionProps {
  estimateId: string;
  sparePartsView?: string;
}

const DetailsSection = ({ estimateId, sparePartsView }: DetailsSectionProps) => {
  const { t } = useTranslation();
  const isB2B = useIsB2B();
  const references = useSelector((state: RootState) => getReferences(state, estimateId));
  const b2bPrices = useSelector((state: RootState) => getEstimateB2BPrices(state, estimateId));
  const b2bDiscounts = useSelector((state: RootState) => getEstimateB2BDiscounts(state, estimateId));

  const referencesSectionPriceVatExcl = Number(
    useSelector((state: RootState) => getEstimateReferenceSectionPriceVatExcl(state, estimateId) ?? 0),
  );
  const operationReferencesSectionPriceVatExcl = Number(
    useSelector((state: RootState) => getEstimateOperationReferenceSectionPriceVatExcl(state, estimateId) ?? 0),
  );
  const referencesPriceVatExcl = referencesSectionPriceVatExcl + operationReferencesSectionPriceVatExcl;
  const referencesSectionVat = Number(
    useSelector((state: RootState) => getEstimateReferenceSectionVat(state, estimateId)),
  );
  const operationReferencesSectionVat = Number(
    useSelector((state: RootState) => getEstimateOperationReferencesVat(state, estimateId)),
  );
  const referencesVat = referencesSectionVat + operationReferencesSectionVat;

  const laborTimeSectionPriceVatExcl = Number(
    useSelector((state: RootState) => getEstimateLaborTimeSectionPriceVatExcl(state, estimateId)),
  );
  const operationLaborTimeSectionPriceVatExcl = Number(
    useSelector((state: RootState) => getEstimateOperationLaborTimesSectionPriceVatExcl(state, estimateId)),
  );
  const laborTimePriceVatExcl = laborTimeSectionPriceVatExcl + operationLaborTimeSectionPriceVatExcl;

  const laborTimeSectionVat = Number(
    useSelector((state: RootState) => getEstimateLaborTimeSectionVat(state, estimateId)),
  );
  const operationLaborTimeSectionVat = Number(
    useSelector((state: RootState) => getEstimateOperationLaborTimesSectionVat(state, estimateId)),
  );
  const laborTimeVat = laborTimeSectionVat + operationLaborTimeSectionVat;

  const bundleOrRecyclingPriceVatExcl = useSelector((state: RootState) =>
    getEstimateBundleOrRecyclingSectionPriceVatExcl(state, estimateId),
  );
  const bundleOrRecyclingVat = useSelector((state: RootState) =>
    getEstimateBundleOrRecyclingSectionVat(state, estimateId),
  );

  const totalMargin = Number(useSelector((state: RootState) => getTotalMargin(state, estimateId)) ?? 0);
  const totalMarginPercentage = Number(
    useSelector((state: RootState) => getTotalMarginPercentage(state, estimateId)) ?? 0,
  );
  const currency = useSelector(getCurrency);
  const sumDiscountMargins = useSelector((state: RootState) =>
    getEstimateReferenceDiscountSumMargin(state, estimateId),
  );
  const totalMarginDiscounts = sumDiscountMargins ? totalMargin + sumDiscountMargins : totalMargin;
  const totalMarginPercentagesDiscounts = sumDiscountMargins
    ? (totalMarginDiscounts / referencesPriceVatExcl) * 100
    : totalMarginPercentage;

  return (
    <LightCard title={t('order.details', 'Order details')}>
      <Flex direction={'column'} data-cy={'order-details'}>
        <DetailsSectionLine
          sectionName={t('catalog.reference_plural', 'References')}
          currency={currency}
          totalVatExcl={
            isB2B
              ? Number(getTotalVatExclPriceForClient(references, b2bPrices, b2bDiscounts).getValue())
              : Number(referencesPriceVatExcl)
          }
          vat={
            isB2B
              ? Number(getTotalVatPriceForClient(references, b2bPrices, b2bDiscounts).getValue())
              : Number(referencesVat)
          }
        />
        <B2BComponent
          b2c={
            <>
              <DetailsSectionLine
                sectionName={t('catalog.parts.category.car_parts.labor_time', 'Labor time')}
                currency={currency}
                totalVatExcl={Number(laborTimePriceVatExcl)}
                vat={Number(laborTimeVat)}
              />
              <DetailsSectionLine
                sectionName={t('estimate.bundle_or_recycling.title', 'Bundles/Recycling')}
                currency={currency}
                totalVatExcl={Number(bundleOrRecyclingPriceVatExcl)}
                vat={Number(bundleOrRecyclingVat)}
              />
            </>
          }
        />
        {sparePartsView === GarageView && (
          <TotalMarginLine
            currency={currency}
            totalMargin={totalMarginDiscounts}
            totalMarginPercentage={textFormatter.formatNumber(totalMarginPercentagesDiscounts, 2)}
          />
        )}
      </Flex>
    </LightCard>
  );
};

export default DetailsSection;
